/**
 * Permission codes the Financial Structures screens (FSD 1.2.7 — Chart of
 * Accounts half) declare an interest in.
 *
 * These are the dot-strings `server/internal/coa/permissions.go` registers
 * with the RBAC catalogue (CLAUDE.md §6) — grep that file for the constants
 * this mirrors. They live beside the module the way
 * `web/src/modules/org/permissions.ts` and
 * `web/src/modules/enterprise/reference-sets/permissions.ts` do, one file per
 * module, matching the server's own package boundary.
 *
 * FSD 1.2.7.1's actor table splits accountability three ways and
 * `permissions.go`'s own comment follows it rather than collapsing everything
 * into one "manage" flag: the Finance Controller "is accountable for the
 * chart of accounts" (`structureManage`), the Enterprise Administrator
 * "configures segment value sets, cross-validation rules and segment security
 * under Finance direction" (`valueSetManage`, `ruleManage`) — three distinct
 * write permissions, so a screen that only holds one of them still shows
 * every read surface but withholds only the write it does not have.
 */

export const COA_PERMISSIONS = {
  /** Views charts of accounts and their segment structure. */
  structureRead: 'coa.structure.read',
  /**
   * Defines and activates a chart of accounts and its segments (FSD 1.2.7.12:
   * the Finance Controller's seat). Also guards the ONE correction path a
   * frozen structure keeps open — the status transition
   * (`ERR_SEGMENT_STRUCTURE_FROZEN` refuses a rename or a segment edit, not a
   * status change, per `structure.go`'s `UpdateStructure`).
   */
  structureManage: 'coa.structure.manage',

  /** Views value sets and the values inside them. */
  valueSetRead: 'coa.value_set.read',
  /**
   * Creates value sets and adds, changes or end-dates their values (FSD
   * 1.2.7.11's "A segment value is added, changed or end-dated"). A value is
   * never edited in place once created — it is end-dated and a new one
   * opened — because postings already carry it as an identity.
   */
  valueSetManage: 'coa.value_set.manage',

  /** Views cross-validation and segment security rules. */
  ruleRead: 'coa.rule.read',
  /**
   * Configures cross-validation rules (F-6.002) and segment security rules
   * (F-6.003) — the Enterprise Administrator's seat.
   */
  ruleManage: 'coa.rule.manage',

  /**
   * Runs the combination check of FND-F07-V04 — a read dressed as a verb
   * (the same reasoning org's `budget.check` carries, per `permissions.go`'s
   * own comment): anyone who may raise a document whose combination gets
   * validated must hold it, or entry would 403 on a permission nobody would
   * think to look for.
   */
  combinationValidate: 'coa.combination.validate',
} as const

export type CoaPermission = (typeof COA_PERMISSIONS)[keyof typeof COA_PERMISSIONS]
